/**
 * MobileBrokerCreate Component
 *
 * Mobile page for creating a new broker.
 * Route: /settings/portfolio/reference-data/brokers/new
 *
 * Follows MobileStrategyCreate pattern with broker-specific fields:
 * name (required), description (optional), default account currency (defaults to USD).
 * Uses 2-stage error handling: hard commit for broker creation, soft post-actions for
 * reload/select/navigate with warning fallback.
 */

// 1. External dependencies
import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';

// 2. Workspace packages
import {
  Button,
  Input,
  TextArea,
  Alert,
  useDisclosure,
  useToastNotification,
  Breadcrumbs,
  BreadcrumbItem,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
} from '@enfomentis/ui';

// 3. Services
import { brokerService } from '@/services/BrokerService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';

// 4. Internal utilities
import { getCurrencyInfo } from '@/utils';
import { CURRENCY_DATABASE } from '@/constants';

// 5. Shared components
import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser/CurrencyChooserModal';
import { UnsavedChangesDialog } from '@/components/shared/UnsavedChangesDialog';

// 6. Context
import { useBrokers } from '../Brokers/context';

/** Route constants */
const BROKER_LIST_ROUTE = '/settings/portfolio/reference-data/brokers';
const SETTINGS_ROUTE = '/settings';
const PORTFOLIO_ROUTE = '/settings/portfolio';

export const MobileBrokerCreate: React.FC = () => {
  const navigate = useNavigate();
  const unsavedChangesDialog = useDisclosure();
  const { showToast } = useToastNotification();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  // Context actions for post-creation state management
  const { reloadBrokers, setShowArchived, selectBroker } = useBrokers();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultAccountCurrency, setDefaultAccountCurrency] = useState('USD');

  // Validation and submission state
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Currency chooser modal state
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);

  // Dirty state — any field differs from initial baseline
  const isDirty =
    name.trim() !== '' ||
    description.trim() !== '' ||
    defaultAccountCurrency !== 'USD';

  // Pending navigation target when showing unsaved changes dialog
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Currency display derivation
  const currencyInfo = getCurrencyInfo(defaultAccountCurrency, CURRENCY_DATABASE);
  const currencyDisplay = currencyInfo
    ? `${currencyInfo.symbol ? `${currencyInfo.symbol} ` : ''}${currencyInfo.name} (${defaultAccountCurrency})`
    : defaultAccountCurrency;

  // --- Navigation handlers ---

  const handleNavigateWithDirtyCheck = useCallback(
    (target: string) => {
      if (isSubmittingRef.current) return;
      if (isDirty) {
        setPendingNavigation(target);
        unsavedChangesDialog.onOpen();
      } else {
        navigate(target);
      }
    },
    [isDirty, navigate, unsavedChangesDialog],
  );

  const handleBack = useCallback(() => {
    handleNavigateWithDirtyCheck(BROKER_LIST_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleNavigateToSettings = useCallback(() => {
    handleNavigateWithDirtyCheck(SETTINGS_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleNavigateToPortfolio = useCallback(() => {
    handleNavigateWithDirtyCheck(PORTFOLIO_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleCancel = useCallback(() => {
    handleNavigateWithDirtyCheck(BROKER_LIST_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  // Handle discard from dialog
  const handleDiscard = useCallback(() => {
    unsavedChangesDialog.onClose();
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [unsavedChangesDialog, navigate, pendingNavigation]);

  // --- Form handlers ---

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      if (nameError) {
        setNameError(null);
      }
    },
    [nameError],
  );

  const handleCurrencyChange = useCallback((currencyCode: string) => {
    setDefaultAccountCurrency(currencyCode);
    setShowCurrencyChooser(false);
  }, []);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      nameInputRef.current?.focus();
      return false;
    }
    setNameError(null);
    return true;
  }, [name]);

  // --- Create handler (2-stage error handling) ---

  const handleCreate = useCallback(async () => {
    // Double-tap guard (ref-lock — synchronous, immune to same-frame races)
    if (isSubmittingRef.current) return;

    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    // Lock acquired only after validation passes (no deadlock on validation failure)
    isSubmittingRef.current = true;

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    let createdBrokerId: string;

    // Stage 1: Hard commit — create broker (failure = user retries)
    try {
      setIsSubmitting(true);

      const createData: Parameters<typeof brokerService.add>[0] = {
        name: trimmedName,
        feeMetadata: {
          defaultAccountCurrency,
        },
      };

      if (trimmedDescription) {
        createData.description = trimmedDescription;
      }

      const newBroker = await brokerService.add(createData);
      createdBrokerId = newBroker.id;
    } catch (error) {
      console.error('Failed to create broker:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create broker';
      setSubmitError(errorMessage);
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      return;
    }

    // Stage 2: Soft post-actions — notify/reload/select/navigate (failure = warning, still navigate)
    try {
      syncCoordinatorService.notifyChange('brokers', createdBrokerId);
      await reloadBrokers();
      setShowArchived(false);
      selectBroker(createdBrokerId);

      showToast({
        title: 'Broker Created',
        description: `"${trimmedName}" has been created`,
        color: 'success',
      });
    } catch (error) {
      console.warn('Post-create actions failed:', error);
      showToast({
        title: 'Refresh Failed',
        description: 'Broker created, but list refresh failed — try switching tabs',
        color: 'warning',
      });
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      navigate(`${BROKER_LIST_ROUTE}/${createdBrokerId}`);
    }
  }, [
    name,
    description,
    defaultAccountCurrency,
    validateForm,
    reloadBrokers,
    setShowArchived,
    selectBroker,
    showToast,
    navigate,
  ]);

  return (
    <>
      <Card radius="none" shadow="none" className="h-full flex flex-col">
        {/* Header — Breadcrumb Navigation + Page Title */}
        <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
          <div className="flex items-center">
            <Breadcrumbs variant="light" size="sm">
              <BreadcrumbItem
                onPress={handleNavigateToSettings}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Settings
              </BreadcrumbItem>
              <BreadcrumbItem
                onPress={handleNavigateToPortfolio}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Portfolio
              </BreadcrumbItem>
              <BreadcrumbItem
                onPress={handleBack}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Brokers
              </BreadcrumbItem>
            </Breadcrumbs>
          </div>
          <h1 className="text-lg font-semibold text-text-primary mt-1">New Broker</h1>
        </CardHeader>

        {/* Content */}
        <CardBody className="flex-1 px-1 pb-2">
          <Card>
            <CardBody className="space-y-4">
              {/* Name (required) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Name <span className="text-danger">*</span>
                </label>
                <Input
                  ref={nameInputRef}
                  placeholder="Enter broker name"
                  value={name}
                  onValueChange={handleNameChange}
                  isInvalid={!!nameError}
                  errorMessage={nameError}
                  autoFocus
                  className="[&_input]:text-base"
                />
              </div>

              {/* Description (optional) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Description
                </label>
                <TextArea
                  placeholder="Notes about this broker account..."
                  value={description}
                  onValueChange={setDescription}
                  minRows={2}
                  maxRows={4}
                  className="[&_textarea]:text-base"
                />
              </div>

              {/* Default Account Currency */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-text-secondary">
                  Default Account Currency
                </label>
                <Button
                  variant="bordered"
                  className="w-full justify-between text-base font-normal"
                  onPress={() => setShowCurrencyChooser(true)}
                  isDisabled={isSubmitting}
                  endContent={
                    <Globe className="w-4 h-4 shrink-0 text-text-tertiary" />
                  }
                >
                  {currencyDisplay}
                </Button>
              </div>

              {/* Error Alert */}
              {submitError && (
                <Alert color="danger" variant="flat">
                  {submitError}
                </Alert>
              )}
            </CardBody>
          </Card>
        </CardBody>

        {/* Footer */}
        <CardFooter className="flex items-center justify-end gap-1 pt-2 border-t border-default-200">
          <Button
            variant="flat"
            size="sm"
            onPress={handleCancel}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            size="sm"
            onPress={handleCreate}
            isLoading={isSubmitting}
          >
            Create
          </Button>
        </CardFooter>
      </Card>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={unsavedChangesDialog.isOpen}
        onClose={unsavedChangesDialog.onClose}
        onDiscard={handleDiscard}
      />

      {/* Currency Chooser Modal */}
      <CurrencyChooserModal
        isOpen={showCurrencyChooser}
        onClose={() => setShowCurrencyChooser(false)}
        selectedCurrency={defaultAccountCurrency}
        onCurrencySelect={handleCurrencyChange}
        title="Default Account Currency"
      />
    </>
  );
};
